import { useMemo, useState } from 'react';

import type { IWorkerQC } from '@/api/worker';

type SortOrder = 'asc' | 'desc';
type SortField =
  | 'full_name'
  | 'total_projects'
  | 'total_tc'
  | 'total_created_issues'
  | (typeof ALL_STATUSES)[number];

interface WorkerQCPanelProps {
  data: IWorkerQC[];
  onQCClick: (qc: IWorkerQC) => void;
}

const ALL_STATUSES = [
  'PASSED',
  'FAILED',
  'BLOCKED',
  'UNTESTED',
  'RETEST',
  'NOT_APPLICABLE',
  'IN_PROGRESS',
  'HOLD',
] as const;

const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

const getHeaderStatusClass = (status: string) => {
  switch (status.toUpperCase()) {
  case 'PASSED':
    return 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border-emerald-200';
  case 'FAILED':
    return 'bg-rose-100 hover:bg-rose-200 text-rose-800 border-rose-200';
  case 'BLOCKED':
    return 'bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-200';
  case 'UNTESTED':
    return 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200';
  case 'RETEST':
    return 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border-yellow-200';
  case 'NOT_APPLICABLE':
    return 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border-indigo-200';
  case 'IN_PROGRESS':
    return 'bg-sky-100 hover:bg-sky-200 text-sky-800 border-sky-200';
  case 'HOLD':
    return 'bg-pink-100 hover:bg-pink-200 text-pink-800 border-pink-200';
  default:
    return 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200';
  }
};

const getStatusClass = (status: string) => {
  switch (status.toUpperCase()) {
  case 'PASSED':
    return 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100';
  case 'FAILED':
    return 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100';
  case 'BLOCKED':
    return 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100';
  case 'UNTESTED':
    return 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100';
  case 'RETEST':
    return 'bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100';
  case 'NOT_APPLICABLE':
    return 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100';
  case 'IN_PROGRESS':
    return 'bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100';
  case 'HOLD':
    return 'bg-pink-50 text-pink-700 border border-pink-200 hover:bg-pink-100';
  default:
    return 'bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100';
  }
};

export default function WorkerQCPanel({ data, onQCClick }: WorkerQCPanelProps) {
  const [qcSearch, setQcSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('total_projects');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (field: SortField) => {
    setCurrentPage(1);
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const filteredQC = useMemo(() => {
    const searched = [...data].filter(qc =>
      qc.full_name.toLowerCase().includes(qcSearch.toLowerCase().trim()),
    );

    return searched.sort((a, b) => {
      let comparison = 0;

      if (sortField === 'full_name') {
        comparison = a.full_name.localeCompare(b.full_name);
      } else if (sortField === 'total_projects') {
        comparison = a.total_projects - b.total_projects;
      } else if (sortField === 'total_tc') {
        const totalA = ALL_STATUSES.reduce(
          (sum, s) => sum + (a.test_runs[s] ?? 0),
          0,
        );
        const totalB = ALL_STATUSES.reduce(
          (sum, s) => sum + (b.test_runs[s] ?? 0),
          0,
        );
        comparison = totalA - totalB;
      } else if (sortField === 'total_created_issues') {
        comparison = a.total_created_issues - b.total_created_issues;
      } else {
        const valA = a.test_runs[sortField] ?? 0;
        const valB = b.test_runs[sortField] ?? 0;
        comparison = valA - valB;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, qcSearch, sortField, sortOrder]);

  const paginatedQC = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredQC.slice(start, start + itemsPerPage);
  }, [filteredQC, currentPage]);

  const totalPages = Math.ceil(filteredQC.length / itemsPerPage) || 1;

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1 text-xs opacity-90">
        {sortOrder === 'desc' ? '▼' : '▲'}
      </span>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-slate-200 bg-white shadow-sm font-sans">
      {/* Header Panel */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4 bg-slate-50/50">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-bold text-slate-800">
            Rekap QC & Admin
          </h2>
          <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
            {filteredQC.length} Pengguna
          </span>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={qcSearch}
            onChange={event => {
              setQcSearch(event.target.value);
              setCurrentPage(1);
            }}
            placeholder="Cari nama QC / Admin..."
            className="w-64 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm"
          />
        </div>
      </div>

      {/* Tabel Data */}
      <div className="min-h-0 flex-1 overflow-auto bg-white">
        <table className="w-full min-w-max text-sm border-collapse">
          <thead className="sticky top-0 z-[1] bg-slate-50 text-slate-700 border-b border-slate-200 shadow-2xs">
            <tr>
              <th
                onClick={() => handleSort('full_name')}
                className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold tracking-wider border-r border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition"
              >
                NAMA PENGGUNA {renderSortIndicator('full_name')}
              </th>

              <th
                onClick={() => handleSort('total_projects')}
                className="whitespace-nowrap px-4 py-3 text-center text-xs font-bold tracking-wider border-r border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition"
              >
                PROJECT {renderSortIndicator('total_projects')}
              </th>

              {ALL_STATUSES.map(status => (
                <th
                  key={status}
                  onClick={() => handleSort(status)}
                  className={`whitespace-nowrap px-3 py-3 text-center text-xs font-bold tracking-wider border-r border-slate-200 cursor-pointer transition ${getHeaderStatusClass(
                    status,
                  )}`}
                >
                  {formatStatus(status)} {renderSortIndicator(status)}
                </th>
              ))}

              <th
                onClick={() => handleSort('total_tc')}
                className="whitespace-nowrap px-4 py-3 text-center text-xs font-bold tracking-wider border-r border-slate-200 bg-slate-200 text-slate-800 hover:bg-slate-300 cursor-pointer transition"
              >
                Total TC {renderSortIndicator('total_tc')}
              </th>

              <th
                onClick={() => handleSort('total_created_issues')}
                className="whitespace-nowrap px-4 py-3 text-center text-xs font-bold tracking-wider bg-rose-100 text-rose-800 border-l border-rose-200 hover:bg-rose-200 cursor-pointer transition"
              >
                Issues {renderSortIndicator('total_created_issues')}
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200 bg-white">
            {paginatedQC.map(qc => {
              const totalTC = ALL_STATUSES.reduce(
                (sum, status) => sum + (qc.test_runs[status] ?? 0),
                0,
              );

              return (
                <tr
                  key={qc.user_id}
                  className="group transition-colors hover:bg-blue-50 "
                >
                  {/* Nama Pengguna */}
                  <td className="px-6 py-2.5 border-r border-slate-100">
                    <button
                      type="button"
                      onClick={() => onQCClick(qc)}
                      className="text-left text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {qc.full_name || '-'}
                    </button>
                  </td>

                  {/* Project */}
                  <td className="px-4 py-2.5 text-center text-sm font-medium text-slate-700 border-r border-slate-100">
                    {qc.total_projects}
                  </td>

                  {/* Status Badges */}
                  {ALL_STATUSES.map(status => (
                    <td
                      key={status}
                      className="px-3 py-2.5 text-center border-r border-slate-100"
                    >
                      <span
                        className={`inline-flex min-w-9 justify-center rounded-md px-2.5 py-1 text-sm font-bold ${getStatusClass(
                          status,
                        )}`}
                      >
                        {qc.test_runs[status] ?? 0}
                      </span>
                    </td>
                  ))}

                  {/* Total TC */}
                  <td className="px-4 py-2.5 text-center text-sm font-bold text-slate-700 border-r border-slate-100">
                    {totalTC}
                  </td>

                  {/* Issues */}
                  <td className="px-4 py-2.5 text-center">
                    <span className="inline-flex min-w-9 justify-center rounded-md bg-rose-50 border border-rose-200 px-2.5 py-1 text-sm font-bold text-rose-700 hover:bg-rose-100 transition-colors cursor-default">
                      {qc.total_created_issues}
                    </span>
                  </td>
                </tr>
              );
            })}

            {paginatedQC.length === 0 && (
              <tr>
                <td
                  colSpan={ALL_STATUSES.length + 4}
                  className="px-6 py-12 text-center text-sm text-slate-500 bg-white"
                >
                  Tidak ada data QC atau Admin pada periode ini.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex shrink-0 items-center justify-between border-t border-slate-200 px-6 py-3 bg-slate-50 text-xs text-slate-600">
        <div>
          Menampilkan{' '}
          <span className="font-semibold">
            {filteredQC.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
          </span>{' '}
          sampai{' '}
          <span className="font-semibold">
            {Math.min(currentPage * itemsPerPage, filteredQC.length)}
          </span>{' '}
          dari <span className="font-semibold">{filteredQC.length}</span> data
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 shadow-2xs transition"
          >
            Previous
          </button>

          <span className="px-2 font-medium">
            Hal {currentPage} dari {totalPages}
          </span>

          <button
            type="button"
            onClick={() =>
              setCurrentPage(prev => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || totalPages === 0}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 shadow-2xs transition"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
